import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { formatQualityScore } from '@/lib/audioQualityScore';
import { cn } from '@/lib/utils';
import type { AudioQualityIndicatorProps } from '@/types/audioQuality';
import { AudioLines } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

const WARNING = 'Tal getur verið óskýrt. Athugaðu hljóðnema og staðsetningu.';
const DESCRIPTION =
  'Bráðabirgðamat á skýrleika tals, ekki mat á nákvæmni umritunar. Matið staðfestir ekki að allir þátttakendur heyrist.';

export const AudioQualityIndicator = ({ quality, compact = false }: AudioQualityIndicatorProps) => {
  const poor = quality.state === 'assessed' && quality.poor;
  const previousPoor = useRef(false);
  const [announcement, setAnnouncement] = useState('');
  useEffect(() => {
    if (poor !== previousPoor.current) {
      setAnnouncement(poor ? WARNING : 'Viðvörun um hljóðgæði er ekki virk.');
      previousPoor.current = poor;
    }
  }, [poor]);
  const score = quality.state === 'assessed' || quality.state === 'paused' ? quality.score : null;
  // Only a scored reading is shown: checking, unavailable, stale and too little speech stay hidden.
  if (score == null) return null;

  const scoreText = formatQualityScore(score);
  const status = quality.state === 'paused' ? 'Í bið' : '';
  const label = `Hljóðgæði ${scoreText}${status ? ` · ${status}` : ''}`;
  // Compact keeps the score visible and lets the words appear when the panel has room.
  const roomy = 'hidden @min-[34rem]:inline';
  const indicator = (
    <button
      type="button"
      aria-label={compact ? label : undefined}
      className={cn(
        'inline-flex min-w-0 items-center gap-1.5 rounded-md border border-transparent px-2 py-1 text-muted-foreground text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        compact && 'h-8 shrink-0 tabular-nums',
        poor &&
          'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200'
      )}
    >
      <AudioLines className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span className="min-w-0 text-left">
        {compact ? (
          <>
            <span className={roomy}>Hljóðgæði </span>
            {scoreText}
            {status ? <span className={roomy}>{` · ${status}`}</span> : null}
          </>
        ) : (
          label
        )}
      </span>
    </button>
  );
  return (
    <>
      {poor ? (
        <Popover>
          <PopoverTrigger asChild>{indicator}</PopoverTrigger>
          <PopoverContent
            align="end"
            className="max-w-[calc(100vw-2rem)] space-y-3 text-sm"
            aria-label="Af hverju viðvörun um hljóðgæði?"
          >
            <p className="font-medium">Viðvarandi óskýrt tal</p>
            <p>Tal hefur ítrekað verið metið óskýrt í hljóðbútum úr upptökunni.</p>
            <p>Prófaðu að færa hljóðnemann nær þeim sem talar og kanna hljóðstillingar.</p>
            <p className="text-muted-foreground text-xs">
              Matið staðfestir ekki hvort orsökin sé bergmál, hávaði eða annað. {DESCRIPTION}
            </p>
          </PopoverContent>
        </Popover>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>{indicator}</TooltipTrigger>
          <TooltipContent className="max-w-xs">{DESCRIPTION}</TooltipContent>
        </Tooltip>
      )}
      <output aria-live="polite" className="sr-only">
        {announcement}
      </output>
    </>
  );
};
